import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

const groups = [
  {
    title: "Company",
    fields: [
      { name: "company_name", label: "Company Name" },
      { name: "tagline", label: "Tagline" },
      { name: "about_short", label: "Short About", type: "textarea" },
    ],
  },
  {
    title: "Contact",
    fields: [
      { name: "phone", label: "Phone" },
      { name: "email", label: "Email" },
      { name: "whatsapp", label: "WhatsApp Number (with country code, digits only)" },
      { name: "address", label: "Address", type: "textarea" },
      { name: "map_embed", label: "Google Maps — paste an address, lat,lng, a Maps link, or an embed URL", type: "textarea" },
    ],
  },
  {
    title: "Social Links",
    fields: [
      { name: "facebook", label: "Facebook URL" },
      { name: "instagram", label: "Instagram URL" },
      { name: "linkedin", label: "LinkedIn URL" },
    ],
  },
  {
    title: "SEO",
    fields: [
      { name: "seo_title", label: "SEO Title" },
      { name: "seo_description", label: "SEO Description", type: "textarea" },
    ],
  },
];

export default function Settings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Settings | Aimway Admin";
    api.get("/settings").then(({ data }) => setForm(data || {})).catch(() => setForm({}));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved. Refresh the site to see changes.");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-brand" /></div>;

  return (
    <div data-testid="admin-settings">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="font-display text-2xl font-800 text-brand-navy">Website Settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">Global information used across your website.</p>
        </div>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-700 text-white hover:bg-brand-navy disabled:opacity-70" data-testid="settings-save">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Changes
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {groups.map((g) => (
          <div key={g.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
            <h2 className="mb-5 font-display text-lg font-700 text-brand-navy">{g.title}</h2>
            <div className="space-y-4">
              {g.fields.map((f) => (
                <div key={f.name} className="space-y-2">
                  <Label className="text-slate-700">{f.label}</Label>
                  {f.type === "textarea" ? (
                    <Textarea value={form[f.name] || ""} onChange={set(f.name)} rows={2} className="border-slate-200 focus-visible:ring-brand" data-testid={`setting-${f.name}`} />
                  ) : (
                    <Input value={form[f.name] || ""} onChange={set(f.name)} className="border-slate-200 focus-visible:ring-brand" data-testid={`setting-${f.name}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
